/**
 * The music player: a queue, two decks, and the rules for moving between them.
 *
 * **Two decks, so songs can overlap.** Each song plays on an <audio> element
 * streamed from the engine (Range requests, so nothing is decoded whole). When
 * the playing song reaches its Early Out -- or, without one, its end minus the
 * crossfade -- the next song starts on the other deck at its Cue In and the two
 * gains cross. With crossfade at 0 it is a gapless hand-over at the Early Out.
 *
 * **Volume levelling from measured loudness.** Every analysed track has an
 * integrated loudness (LUFS). Each song's gain is set so it plays at the same
 * perceived level (-14 LUFS), limited to +6/-12 dB so a quiet live recording is
 * not pushed into clipping. Unanalysed tracks play unchanged.
 *
 * **One sound at a time.** Other players in the app (the Flow preview, the cue
 * editor) announce `audio.claim` on the bus before they start; this player
 * pauses when another claims, and claims before it plays.
 *
 * **More than one player.** `player` is the one the library plays on. A plugin
 * that puts music on air (Radio) makes its own with `createPlayer`: its own
 * queue, volume, crossfade and output, its own bus events (`<events>.track`…),
 * and it neither pauses for nor silences anything else -- what is on air must
 * never stop because someone previewed a song at the desk.
 *
 * State is published as an immutable snapshot for `usePlayer()`, and as bus
 * events (`player.track`, `player.state`) for plugins.
 */

import { useRef, useSyncExternalStore } from "react";
import { audioUrl, engineBase } from "../api";
import { mixPoint } from "./handover";
import { withToken } from "./session";
import { audioOutput, resetOutput } from "./audioCache";
import type { Track } from "../types";
import { bus } from "./bus";
import { readPref, writePref } from "./prefs";

export type Repeat = "off" | "all" | "one";

export interface QueueTrack {
  id: string;
  title: string;
  artist: string;
  album: string | null;
  /** Optional: queues saved before it was kept have none. */
  year?: number | null;
  bpm: number | null;
  musicalKey: string | null;
  lufs: number | null;
  /** The song's peak, dBFS (optional, as `year`): levelling never boosts it past {@link PEAK_CEILING_DB}. */
  peak?: number | null;
  cueIn: number | null;
  cueOut: number | null;
  /** Where the next song starts when the mix point is in force (core/handover.ts). Optional, as `year`. */
  mixAt?: number | null;
  /** Where the vocal comes in (the Intro point), for a talk-over countdown. Optional, as `year`. */
  introEnd?: number | null;
  duration: number;
}

export interface QueueItem {
  key: string;
  track: QueueTrack;
  /** Placed by hand: stays where it is when a flow or Auto DJ refills the queue. */
  pinned?: boolean;
  /**
   * Chosen by an automatic planner (Radio's Auto DJ), not by a person: it may be
   * taken out and chosen again when what it was planned from changes.
   */
  auto?: boolean;
  /** Why it was queued, for the play log (Radio's rules use `rule:<id>`). */
  reason?: string;
  /** Fade out over this many seconds before the item's mix out (a song shortened to land on time). */
  fadeOut?: number;
  /** Seconds this item was shortened by (shown in Up next). */
  trimmed?: number;
  /** Clips that play over this song, `at` seconds into the file (Radio's rules). */
  overlays?: Overlay[];
  /** Where it came from, shown beside it (Radio: the show whose playlist or flow queued it). */
  from?: string;
  /**
   * Waits for Play: the item before it plays to its end and this one is made
   * ready, as in Live Assist, whatever Automation says. Set by hand (Radio's
   * arrow in the log).
   */
  hold?: boolean;
}

export interface Overlay {
  track: QueueTrack;
  reason?: string;
  /** File seconds of the song where the clip starts. */
  at: number;
  placement?: string;
}

/** How far the song dips under a clip played over it (the Radio layout's Duck uses the same 12 dB). */
export const OVERLAY_DIP_DB = 12;

/**
 * Where the queue came from, when it came from something that keeps feeding it:
 * a flow re-picks the songs after the current one when its curves change, and
 * tops the queue up as it runs out. Playing anything else clears it.
 */
export interface QueueSource {
  kind: "flow";
  /** Identifies the one flow screen that owns this queue. */
  key: string;
  label: string;
}

export interface PlayerSnapshot {
  queue: QueueItem[];
  source: QueueSource | null;
  index: number;
  current: QueueItem | null;
  playing: boolean;
  loading: boolean;
  position: number;
  duration: number;
  volume: number;
  muted: boolean;
  shuffle: boolean;
  repeat: Repeat;
  crossfade: number;
  levelling: boolean;
  /** Pause when the current song ends instead of moving on. */
  stopAfter: boolean;
  /**
   * True: one item runs on into the next by itself. False is Live Assist: an item
   * plays to its end and the next waits, ready, until someone presses Play.
   */
  automation: boolean;
  /** Music lowered by 12 dB for talking over. */
  ducked: boolean;
  error: string | null;
}

const TARGET_LUFS = -14;
const MAX_BOOST_DB = 6;
const MAX_CUT_DB = -12;
/** Levelling boosts a quiet song only as far as its loudest sample stays under this (dBFS): no distortion. */
export const PEAK_CEILING_DB = -1;
const TICK_MS = 200;
/** The deck clips played over a song use. */
const OVER = 2;
/** The longest Play waits for the sound to wake before building it again (ms). */
const WAKE_MS = 2000;
export interface PlayerOptions {
  /** Who this player is, for `audio.claim`. */
  id: string;
  /** Prefix of its remembered settings: "nova.player.volume", … */
  prefs: string;
  /** Prefix of its bus events: "player.track", "player.state", … */
  events: string;
  /** The audio output it plays into (core/audioCache): its own, so it can have its own device. */
  output: string;
  /** False: never paused by another sound, and never claims (an on-air player). */
  shared: boolean;
  /** Answers the keyboard's and the OS's media keys. Only one player should. */
  mediaKeys: boolean;
  /**
   * In a crossfade, whether the next song rises from silence (a music player's
   * blend) or starts at its full level while the one before fades under it (radio).
   */
  fadeIn: boolean;
  /**
   * Keep at most this many played items at the head of the queue (none: keep them
   * all). A station runs for days: every item it ever played stayed in On air's
   * queue, and everything sent or compared over the whole queue grew with it until
   * the window froze. What played is in the play log already.
   */
  keepPlayed?: number;
}

/** Played items are let go in batches this big, so indices shift about once an hour, not every song. */
const TRIM_BATCH = 25;

const LIBRARY: PlayerOptions = { id: "player", prefs: "nova.player", events: "player", output: "main", shared: true, mediaKeys: true, fadeIn: true };

let keySeq = 0;
/** Per window, so a queue carried on in another window (restore) keeps unique keys. */
const KEY_PREFIX = Math.random().toString(36).slice(2, 7);
const nextKey = () => `q${KEY_PREFIX}${++keySeq}`;

export function toQueueTrack(t: Track): QueueTrack {
  return {
    id: t.id,
    title: t.title || "Untitled",
    artist: t.artist || "",
    album: t.album ?? null,
    year: t.year ?? null,
    bpm: t.bpm,
    musicalKey: t.musicalKey,
    lufs: t.lufs,
    peak: t.truePeak ?? null,
    cueIn: t.cueIn,
    cueOut: t.cueOut,
    mixAt: t.mixAt ?? null,
    introEnd: t.introEnd ?? null,
    duration: t.duration,
  };
}

function toItem(t: Track | QueueTrack): QueueItem {
  return { key: nextKey(), track: "path" in t ? toQueueTrack(t) : t };
}

/**
 * The linear gain that brings a track to the target loudness. A boost stops where
 * the song's peak (when known) would pass {@link PEAK_CEILING_DB}: a quiet song with
 * loud peaks is raised less, rather than raised into distortion. Cuts are never limited.
 */
export function levelGain(lufs: number | null, on: boolean, peak: number | null = null): number {
  if (!on || lufs === null || !Number.isFinite(lufs)) return 1;
  let db = Math.min(MAX_BOOST_DB, Math.max(MAX_CUT_DB, TARGET_LUFS - lufs));
  if (db > 0 && peak !== null && Number.isFinite(peak)) db = Math.min(db, Math.max(0, PEAK_CEILING_DB - peak));
  return 10 ** (db / 20);
}

interface Deck {
  el: HTMLAudioElement;
  gain: GainNode | null;
  key: string | null;
}

class Player {
  private readonly o: PlayerOptions;
  private s: PlayerSnapshot = {
    queue: [],
    source: null,
    index: -1,
    current: null,
    playing: false,
    loading: false,
    position: 0,
    duration: 0,
    volume: 0.8,
    muted: false,
    shuffle: false,
    repeat: "off",
    crossfade: 6,
    levelling: true,
    stopAfter: false,
    automation: true,
    ducked: false,
    error: null,
  };
  private analyserNode: AnalyserNode | null = null;
  private listeners = new Set<() => void>();
  private decks: Deck[] = [];
  private active = 0;
  private master: GainNode | null = null;
  /** The songs alone (decks 0 and 1), before the master: what `dip` lowers. Clips over a song and other inputs (carts) bypass it. */
  private music: GainNode | null = null;
  /** Who is dipping the music, and by how many dB; the deepest wins. */
  private dips = new Map<string, number>();
  private ctx: AudioContext | null = null;
  private outMaster: AudioNode | null = null;
  private timer: number | null = null;
  private history: number[] = [];
  /** Jumps under way (they hold an index across awaits): no trimming meanwhile. */
  private jumping = 0;
  private fading = false;
  /** The item whose early fade has started. */
  private fadedKey: string | null = null;
  /** Overlays already started ("<item key>:<index>"). */
  private overlaid = new Set<string>();
  /** Songs that may not play, with why (a plugin sets them: Radio's dirty songs). */
  private unplayable = new Map<string, string>();

  /** Replace the songs that may not play. They are skipped wherever playback would start them. */
  setUnplayable(ids: Iterable<string>, why: string) {
    this.unplayable = new Map([...ids].map((id) => [id, why]));
  }

  isUnplayable(trackId: string): boolean {
    return this.unplayable.has(trackId);
  }
  /** Stops the clip playing over a song (pause, skip), restoring the song's level. */
  private stopOver: (() => void) | null = null;

  /**
   * Play a clip over the song playing: the song dips 12 dB while it plays and
   * comes back after. Reported on the bus as `player.overlay` for the play log.
   */
  async playOver(o: Overlay) {
    await this.graph();
    const over = this.deck(OVER);
    const song = this.decks[this.active];
    const cur = this.s.current;
    if (!song || !cur) return;
    const level = levelGain(cur.track.lufs, this.s.levelling, cur.track.peak ?? null);
    const startedAt = Date.now();
    over.el.src = audioUrl(o.track.id);
    over.el.currentTime = o.track.cueIn ?? 0;
    this.setDeckGain(over, levelGain(o.track.lufs, this.s.levelling, o.track.peak ?? null));
    this.setDeckGain(song, level * 10 ** (-OVERLAY_DIP_DB / 20), 0.4);
    const end = o.track.cueOut ?? o.track.duration;
    let done = false;
    const finish = (finished: boolean) => {
      if (done) return;
      done = true;
      this.stopOver = null;
      over.el.pause();
      over.el.removeEventListener("ended", onEnd);
      over.el.removeEventListener("timeupdate", onTime);
      // Back up, unless the song has moved on (a new song sets its own level).
      if (this.decks[this.active] === song && this.s.current?.key === cur.key && this.fadedKey !== cur.key) {
        this.setDeckGain(song, level, 0.6);
      }
      bus.emit(`${this.o.events}.overlay`, { trackId: o.track.id, reason: o.reason, startedAt, seconds: (Date.now() - startedAt) / 1000, finished });
    };
    this.stopOver?.();
    this.stopOver = () => finish(false);
    const onEnd = () => finish(true);
    const onTime = () => over.el.currentTime >= end - 0.05 && finish(true);
    over.el.addEventListener("ended", onEnd);
    over.el.addEventListener("timeupdate", onTime);
    try {
      await over.el.play();
    } catch {
      finish(false);
    }
  }

  constructor(options: PlayerOptions = LIBRARY) {
    this.o = options;
    const pref = <T,>(key: string, fallback: T) => readPref<T>(`${options.prefs}.${key}`, fallback);
    this.s = {
      ...this.s,
      volume: pref("volume", 0.8),
      shuffle: pref("shuffle", false),
      repeat: pref<Repeat>("repeat", "off"),
      crossfade: pref("crossfade", 6),
      levelling: pref("levelling", true),
      automation: pref("automation", true),
    };
    if (options.shared) {
      bus.on<{ owner: string }>("audio.claim", (e) => {
        if (e.data?.owner !== options.id && this.s.playing) this.pause();
      });
    }
    if (options.mediaKeys) this.installMediaKeys();
  }

  /** Which player this is (`player`, or a plugin's). */
  get id() {
    return this.o.id;
  }

  /** The prefix of this player's bus events ("player", "onair"). */
  get events() {
    return this.o.events;
  }

  /** Announce that this player is about to make sound, so shared players pause. */
  private claim() {
    if (this.o.shared) bus.emit("audio.claim", { owner: this.o.id });
  }

  private remember(key: string, value: unknown) {
    writePref(`${this.o.prefs}.${key}`, value);
  }

  // ---------------------------------------------------------------- store

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  get = () => this.s;

  private set(patch: Partial<PlayerSnapshot>) {
    const before = this.s;
    this.s = { ...this.s, ...patch };
    this.s.current = this.s.queue[this.s.index] ?? null;
    for (const fn of this.listeners) fn();
    if (before.current?.key !== this.s.current?.key) {
      bus.emit(`${this.o.events}.track`, { trackId: this.s.current?.track.id ?? null });
      this.updateMediaSession();
    }
    if (before.playing !== this.s.playing) bus.emit(`${this.o.events}.state`, { playing: this.s.playing });
  }

  // ---------------------------------------------------------------- audio graph

  private deck(i: number): Deck {
    if (!this.decks[i]) {
      const el = new Audio();
      el.crossOrigin = "anonymous";
      el.preload = "auto";
      el.addEventListener("ended", () => this.onEnded(i));
      el.addEventListener("error", () => {
        if (i === this.active) this.set({ error: "This file could not be played.", loading: false });
      });
      el.addEventListener("waiting", () => i === this.active && this.set({ loading: true }));
      el.addEventListener("playing", () => i === this.active && this.set({ loading: false }));
      this.decks[i] = { el, gain: null, key: null };
    }
    return this.decks[i];
  }

  /**
   * Wake the sound before playing: resume a suspended context, but never wait on it for
   * ever. After a station sat idle, Windows can leave the audio device asleep so that
   * resume() never answers, and Play, Next and Automation all waited behind it (owner,
   * 2026-11-02). Not running within WAKE_MS: the sound is built again from scratch.
   */
  private async wake(): Promise<void> {
    await this.graph();
    const ctx = this.ctx;
    if (!ctx || ctx.state === "running") return;
    const woke = await Promise.race([
      ctx.resume().then(() => true, () => false),
      new Promise<boolean>((r) => window.setTimeout(() => r(false), WAKE_MS)),
    ]);
    if (woke && (ctx.state as AudioContextState) === "running") return;
    await this.rebuildAudio();
  }

  /** A new context and new decks (the old elements belong to the old context for good). */
  private async rebuildAudio(): Promise<void> {
    for (const d of this.decks) {
      if (!d) continue;
      d.el.pause();
      d.el.removeAttribute("src");
      d.el.load();
    }
    this.decks = [];
    this.master = null;
    this.music = null;
    this.outMaster = null;
    this.analyserNode = null;
    this.ctx = null;
    // Where the item on air was, so Play carries on from there.
    if (this.s.current) this.resumeAt = this.s.position;
    await resetOutput(this.o.output);
    await this.graph();
    bus.emit(`${this.o.events}.audioRebuilt`, {});
  }

  private async graph(): Promise<void> {
    if (this.master) return;
    try {
      const { ctx, master } = await audioOutput(this.o.output);
      this.ctx = ctx;
      this.outMaster = master;
      this.master = ctx.createGain();
      this.master.connect(master);
      this.music = ctx.createGain();
      this.music.connect(this.master);
      for (const i of [0, 1, OVER]) {
        const d = this.deck(i);
        const src = ctx.createMediaElementSource(d.el);
        d.gain = ctx.createGain();
        src.connect(d.gain).connect(i === OVER ? this.master : this.music);
      }
      this.applyDips(0);
      this.applyVolume();
    } catch {
      // No Web Audio: element volume still works, without levelling precision.
      this.master = null;
    }
  }

  private applyVolume() {
    const v = (this.s.muted ? 0 : this.s.volume) * (this.s.ducked ? 0.25 : 1);
    if (this.master && this.ctx) {
      this.master.gain.cancelScheduledValues(this.ctx.currentTime);
      this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.06);
    } else if (this.master) this.master.gain.value = v;
    else for (const d of this.decks) if (d) d.el.volume = v;
  }

  private setDeckGain(d: Deck, value: number, overSeconds = 0) {
    if (d.gain && this.ctx) {
      const now = this.ctx.currentTime;
      d.gain.gain.cancelScheduledValues(now);
      if (overSeconds > 0) {
        d.gain.gain.setValueAtTime(d.gain.gain.value, now);
        d.gain.gain.linearRampToValueAtTime(value, now + overSeconds);
      } else {
        d.gain.gain.setValueAtTime(value, now);
      }
    } else {
      d.el.volume = Math.min(1, value * (this.s.muted ? 0 : this.s.volume));
    }
  }

  // ---------------------------------------------------------------- queue

  /** Replace the queue and start playing. A `source` keeps the queue tied to what made it. */
  playTracks(tracks: (Track | QueueTrack)[], start = 0, source: QueueSource | null = null) {
    const queue = tracks.map(toItem);
    this.history = [];
    this.set({ queue, index: -1, error: null, source });
    void this.jump(Math.max(0, Math.min(start, queue.length - 1)));
  }

  /**
   * Keep what has played and what is playing; replace everything after it.
   * Only for the source that owns the queue -- returns false otherwise, so a
   * flow never overwrites a queue the person has since replaced.
   */
  replaceUpcoming(tracks: (Track | QueueTrack)[], sourceKey: string, pinned: boolean[] = []): boolean {
    if (this.s.source?.key !== sourceKey) return false;
    const keep = this.s.queue.slice(0, this.s.index + 1);
    const upcoming = tracks.map((t, i) => ({ ...toItem(t), pinned: pinned[i] || undefined }));
    this.set({ queue: [...keep, ...upcoming] });
    return true;
  }

  /**
   * Move a song in the queue by hand (dragging in Up next). The song playing and
   * those already played stay put; the moved song is pinned where it lands.
   */
  move(from: number, to: number) {
    const { queue, index } = this.s;
    if (from <= index || to <= index || from >= queue.length) return;
    const next = [...queue];
    const [item] = next.splice(from, 1);
    next.splice(Math.min(to, next.length), 0, { ...item, pinned: true });
    this.set({ queue: next });
  }

  /** Pin a song where it is, or let it be re-chosen again. */
  setPinned(i: number, on: boolean) {
    const item = this.s.queue[i];
    if (!item) return;
    const queue = [...this.s.queue];
    queue[i] = { ...item, pinned: on || undefined };
    this.set({ queue });
  }

  /** Add to the end for the source that owns the queue (a flow topping itself up). */
  extend(tracks: (Track | QueueTrack)[], sourceKey: string): boolean {
    if (this.s.source?.key !== sourceKey) return false;
    this.set({ queue: [...this.s.queue, ...tracks.map(toItem)] });
    return true;
  }

  enqueue(tracks: (Track | QueueTrack)[]) {
    const items = tracks.map(toItem);
    this.set({ queue: [...this.s.queue, ...items] });
    if (this.s.index < 0 && items.length) void this.jump(0);
  }

  playNext(tracks: (Track | QueueTrack)[]) {
    const items = tracks.map(toItem);
    const at = this.s.index + 1;
    const queue = [...this.s.queue.slice(0, at), ...items, ...this.s.queue.slice(at)];
    this.set({ queue });
    if (this.s.index < 0 && items.length) void this.jump(0);
  }

  /**
   * Add tracks at the end with why each was queued, and optionally an earlier
   * mix out with a fade (Auto DJ landing on a fixed time).
   */
  appendPlanned(entries: { track: QueueTrack; reason?: string; trimTo?: number; fadeOut?: number; overlays?: Overlay[]; from?: string }[],
    { auto = false }: { auto?: boolean } = {}) {
    const items = entries.map((e) => {
      const trimmed = e.trimTo !== undefined && e.track.cueOut !== null ? e.track.cueOut - e.trimTo
        : e.trimTo !== undefined ? e.track.duration - e.trimTo : 0;
      const track = e.trimTo !== undefined ? { ...e.track, cueOut: e.trimTo } : e.track;
      return { ...toItem(track), reason: e.reason, fadeOut: e.fadeOut, trimmed: trimmed > 0 ? trimmed : undefined, overlays: e.overlays, from: e.from, auto: auto || undefined };
    });
    this.set({ queue: [...this.s.queue, ...items] });
    if (this.s.index < 0 && items.length) void this.jump(0);
  }

  /** Put tracks right after the current one, each with why it was queued (Radio's markers and rules). */
  insertNext(entries: { track: QueueTrack; reason?: string }[]) {
    const items = entries.map((e) => ({ ...toItem(e.track), reason: e.reason }));
    const at = this.s.index + 1;
    this.set({ queue: [...this.s.queue.slice(0, at), ...items, ...this.s.queue.slice(at)] });
  }

  /**
   * Put songs in and reorder what follows the item playing, as one change (an
   * insert and the rearranging that suits it, core/arrange.ts). `order` names
   * the upcoming items by key and the new ones by track, from just after the
   * one playing. Keys no longer upcoming are skipped; upcoming items it leaves
   * out keep their order at the end, so nothing queued meanwhile is lost.
   */
  arrangeUpcoming(order: (string | { track: QueueTrack; reason?: string })[]) {
    const { queue, index } = this.s;
    const head = queue.slice(0, index + 1);
    const rest = queue.slice(index + 1);
    const left = new Map(rest.map((x) => [x.key, x]));
    const out: QueueItem[] = [];
    for (const o of order) {
      if (typeof o !== "string") out.push({ ...toItem(o.track), reason: o.reason });
      else if (left.has(o)) {
        out.push(left.get(o)!);
        left.delete(o);
      }
    }
    out.push(...rest.filter((x) => left.has(x.key)));
    this.set({ queue: [...head, ...out] });
    if (this.s.index < 0 && out.length) void this.jump(0);
  }

  /** Take out upcoming items by key (never the one playing or any already played). */
  removeKeys(keys: Set<string>) {
    if (!keys.size) return;
    const { queue, index } = this.s;
    this.set({ queue: queue.filter((x, k) => k <= index || !keys.has(x.key)) });
  }

  removeAt(i: number) {
    if (i === this.s.index) return;
    const queue = this.s.queue.filter((_, k) => k !== i);
    this.set({ queue, index: i < this.s.index ? this.s.index - 1 : this.s.index });
  }

  clear() {
    this.stopAll();
    this.history = [];
    this.set({ queue: [], index: -1, playing: false, position: 0, duration: 0, source: null });
  }

  // ---------------------------------------------------------------- transport

  /** Play queue item `i`. `fade` crossfades over the crossfade setting, or over `seconds` when given. */
  async jump(i: number, opts: { fade?: boolean; seconds?: number; at?: number } = {}) {
    this.jumping += 1;
    try {
      await this.jumpNow(i, opts);
    } finally {
      this.jumping -= 1;
    }
  }

  private async jumpNow(i: number, { fade = false, seconds, at }: { fade?: boolean; seconds?: number; at?: number } = {}) {
    // A song that may not play is passed over for the next one that may.
    let n = i;
    while (this.s.queue[n] && this.unplayable.has(this.s.queue[n].track.id)) {
      const skipped = this.s.queue[n].track;
      bus.emit(`${this.o.events}.skipped`, { trackId: skipped.id, title: skipped.title, why: this.unplayable.get(skipped.id) });
      n += 1;
    }
    if (n !== i) {
      if (!this.s.queue[n]) return this.stopAfterSkipping(fade, seconds);
      i = n;
    }
    const item = this.s.queue[i];
    if (!item) return;
    this.stopOver?.();
    this.claim();
    await this.wake();
    const fromDeck = this.deck(this.active);
    const toIndex = fade ? 1 - this.active : this.active;
    const to = this.deck(toIndex);
    if (this.s.index >= 0 && this.s.index !== i) this.history.push(this.s.index);

    to.el.src = audioUrl(item.track.id);
    to.key = item.key;
    const start = at ?? item.track.cueIn ?? 0;
    this.resumeAt = null;
    const target = levelGain(item.track.lufs, this.s.levelling, item.track.peak ?? null);
    const xf = fade ? Math.max(0.1, seconds ?? this.s.crossfade) : 0;
    const rises = fade && this.o.fadeIn;
    this.setDeckGain(to, rises ? 0 : target);
    this.set({ index: i, loading: true, error: null, position: start, duration: item.track.duration });
    try {
      to.el.currentTime = start;
      await to.el.play();
    } catch (err) {
      // A newer jump replaced this one before it started (two items started in a row):
      // not a fault in the file, and the newer one is already on its way.
      if (err instanceof DOMException && err.name === "AbortError") return;
      this.set({ loading: false, playing: false, error: err instanceof Error ? err.message : String(err) });
      return;
    }
    if (fade) {
      if (rises) this.setDeckGain(to, target, xf);
      this.setDeckGain(fromDeck, 0, xf);
      const old = fromDeck;
      window.setTimeout(() => {
        if (old.key !== this.s.current?.key) old.el.pause();
      }, xf * 1000 + 100);
    } else if (fromDeck !== to) {
      fromDeck.el.pause();
    }
    this.active = toIndex;
    this.fading = false;
    this.set({ playing: true, loading: false });
    this.startTicking();
  }

  play() {
    if (this.s.index < 0) {
      if (this.s.queue.length) void this.jump(0);
      return;
    }
    // Paused on a song that has since been made unplayable: go on to the next.
    if (this.s.current && this.unplayable.has(this.s.current.track.id)) return void this.jump(this.s.index);
    this.claim();
    void this.wake().then(async () => {
      const d = this.deck(this.active);
      if (!d.el.src) return void this.jump(this.s.index, this.resumeAt !== null ? { at: this.resumeAt } : {});
      try {
        await d.el.play();
        this.set({ playing: true });
        this.startTicking();
      } catch (err) {
        this.set({ error: err instanceof Error ? err.message : String(err) });
      }
    });
  }

  /** Nothing after the song playing may play: fade it out (when asked) and stop. */
  private stopAfterSkipping(fade: boolean, seconds?: number) {
    const d = this.decks[this.active];
    if (!fade || !d || !this.s.playing) return this.pause();
    const xf = Math.max(0.1, seconds ?? this.s.crossfade);
    this.setDeckGain(d, 0, xf);
    window.setTimeout(() => {
      this.pause();
      // Back to its level, so pressing play again is not silent.
      this.setDeckGain(d, levelGain(this.s.current?.track.lufs ?? null, this.s.levelling, this.s.current?.track.peak ?? null));
    }, xf * 1000 + 100);
  }

  pause() {
    this.stopOver?.();
    for (const d of this.decks) d?.el.pause();
    this.set({ playing: false });
  }

  toggle() {
    if (this.s.playing) this.pause();
    else this.play();
  }

  next({ fade = false, seconds }: { fade?: boolean; seconds?: number } = {}) {
    const n = this.pickNext();
    if (n === null) {
      this.pause();
      return;
    }
    void this.jump(n, { fade, seconds });
  }

  prev() {
    const d = this.decks[this.active];
    const start = this.s.current?.track.cueIn ?? 0;
    // Like every player: a few seconds in, "previous" restarts the song.
    if (d && d.el.currentTime - start > 3) {
      this.seek(start);
      return;
    }
    const back = this.history.pop();
    if (back !== undefined) void this.jump(back);
    else if (this.s.index > 0) void this.jump(this.s.index - 1);
    else this.seek(start);
  }

  seek(seconds: number) {
    const d = this.decks[this.active];
    if (!d) return;
    d.el.currentTime = Math.max(0, Math.min(seconds, (this.s.duration || d.el.duration || 0) - 0.05));
    this.set({ position: d.el.currentTime });
  }

  /**
   * Where another sound can play through this player's volume, ducking and
   * output meter (Radio's carts), instead of a separate path that ignores all
   * three. Null when Web Audio is unavailable.
   */
  async extraInput(): Promise<{ ctx: AudioContext; input: AudioNode } | null> {
    await this.graph();
    return this.master && this.ctx ? { ctx: this.ctx, input: this.master } : null;
  }

  /**
   * For live voices mixed with this player (Remote Studio's presenters): `air` goes
   * straight to the output, past the volume and the ducking (a voice is never
   * ducked under its own music); `programme` is the music and carts as they go
   * out, to build return feeds from. Null when Web Audio is unavailable.
   */
  async liveInput(): Promise<{ ctx: AudioContext; air: AudioNode; programme: AudioNode } | null> {
    await this.graph();
    return this.master && this.ctx && this.outMaster ? { ctx: this.ctx, air: this.outMaster, programme: this.master } : null;
  }

  setVolume(v: number) {
    const volume = Math.max(0, Math.min(1, v));
    this.remember("volume", volume);
    this.set({ volume, muted: false });
    this.applyVolume();
  }

  toggleMute() {
    this.set({ muted: !this.s.muted });
    this.applyVolume();
  }

  setShuffle(on: boolean) {
    this.remember("shuffle", on);
    this.set({ shuffle: on });
  }

  cycleRepeat() {
    const repeat: Repeat = this.s.repeat === "off" ? "all" : this.s.repeat === "all" ? "one" : "off";
    this.remember("repeat", repeat);
    this.set({ repeat });
  }

  setCrossfade(seconds: number) {
    const crossfade = Math.max(0, Math.min(12, seconds));
    this.remember("crossfade", crossfade);
    this.set({ crossfade });
  }

  /**
   * Lower the songs by `db` for `source` (a cart playing over them, say), or
   * bring them back (`null`). Several at once: the deepest wins. Clips over a
   * song and anything played through `extraInput` are not lowered.
   */
  dip(source: string, db: number | null, seconds = 0.4) {
    if (db === null || db <= 0) this.dips.delete(source);
    else this.dips.set(source, db);
    this.applyDips(seconds);
  }

  private applyDips(seconds: number) {
    if (!this.music || !this.ctx) return;
    const deepest = Math.max(0, ...this.dips.values());
    const now = this.ctx.currentTime;
    this.music.gain.cancelScheduledValues(now);
    this.music.gain.setTargetAtTime(10 ** (-deepest / 20), now, Math.max(0.01, seconds / 3));
  }

  /** Lower the music by 12 dB (talk-over) or bring it back. */
  duck(on: boolean) {
    this.set({ ducked: on });
    this.applyVolume();
  }

  setStopAfter(on: boolean) {
    this.set({ stopAfter: on });
  }

  /** A level meter tap on the output, created on first use. */
  analyser(): AnalyserNode | null {
    if (!this.analyserNode && this.ctx && this.master) {
      this.analyserNode = this.ctx.createAnalyser();
      this.analyserNode.fftSize = 2048;
      // The whole output, live voices (liveInput) included.
      (this.outMaster ?? this.master).connect(this.analyserNode);
    }
    return this.analyserNode;
  }

  setLevelling(on: boolean) {
    this.remember("levelling", on);
    this.set({ levelling: on });
    const d = this.decks[this.active];
    if (d && this.s.current) this.setDeckGain(d, levelGain(this.s.current.track.lufs, on, this.s.current.track.peak ?? null), 0.3);
  }

  // ---------------------------------------------------------------- internals

  private pickNext(): number | null {
    const { queue, index, repeat } = this.s;
    // A flow's order is the point of it: shuffle does not apply to its queue.
    const shuffle = this.s.shuffle && !this.s.source;
    if (!queue.length) return null;
    if (repeat === "one" && index >= 0) return index;
    if (shuffle) {
      const unplayed = queue.map((_, i) => i).filter((i) => i !== index && !this.history.includes(i));
      if (unplayed.length) return unplayed[Math.floor(Math.random() * unplayed.length)];
      if (repeat === "all") {
        this.history = [];
        return Math.floor(Math.random() * queue.length);
      }
      return null;
    }
    if (index + 1 < queue.length) return index + 1;
    return repeat === "all" ? 0 : null;
  }

  private onEnded(deckIndex: number) {
    if (deckIndex !== this.active || this.fading) return;
    if (this.s.stopAfter) {
      this.set({ stopAfter: false, playing: false });
      return;
    }
    if (this.nextWaits()) return this.cueNext();
    this.next();
  }

  /** Whether the next item waits for Play: Live Assist, or it is held (`hold`). */
  private nextWaits(): boolean {
    if (!this.s.automation) return true;
    const n = this.pickNext();
    return n !== null && !!this.s.queue[n]?.hold;
  }

  /**
   * Play `track` over the item playing, `at` seconds into its file (at once if
   * that point has passed), the song dipping under it as for any clip over a
   * song. Returns false when nothing is playing.
   */
  addOverlay(track: QueueTrack, at: number, reason?: string): boolean {
    const { queue, index } = this.s;
    const cur = queue[index];
    if (!cur) return false;
    const when = Math.max(at, this.s.position + 0.2);
    const next = [...queue];
    next[index] = { ...cur, overlays: [...(cur.overlays ?? []), { track, at: when, reason, placement: "point" }] };
    this.set({ queue: next });
    return true;
  }

  /** Make item `i` wait for Play (or run on by itself again). */
  setHold(i: number, on: boolean) {
    const item = this.s.queue[i];
    if (!item || i <= this.s.index) return;
    const queue = [...this.s.queue];
    queue[i] = { ...item, hold: on || undefined };
    this.set({ queue });
  }

  /** Make the next item current, loaded and waiting: Play starts it. */
  private cueNext() {
    const n = this.pickNext();
    for (const d of this.decks) d?.el.pause();
    if (n === null || (n === this.s.index && this.s.repeat !== "one")) {
      this.set({ playing: false });
      return;
    }
    if (this.s.index >= 0) this.history.push(this.s.index);
    // An empty deck makes the next play() start this item from its Cue In.
    const d = this.deck(this.active);
    d.el.removeAttribute("src");
    d.el.load(); // resets its clock, so the old item's position is not read back
    d.key = null;
    const item = this.s.queue[n];
    this.set({ index: n, playing: false, loading: false, position: item?.track.cueIn ?? 0, duration: item?.track.duration ?? 0 });
  }

  /** Automation on (one item runs into the next) or off (Live Assist). */
  setAutomation(on: boolean) {
    this.remember("automation", on);
    this.set({ automation: on });
  }

  private stopping: number | null = null;

  /**
   * Stop the item playing and make the next one ready (Play starts it). Fades out
   * over `seconds`; pressed again while fading, it cuts at once.
   */
  stop(seconds = 1) {
    const d = this.decks[this.active];
    const finish = () => {
      if (this.stopping !== null) window.clearTimeout(this.stopping);
      this.stopping = null;
      this.stopOver?.();
      this.cueNext();
    };
    if (this.stopping !== null || !this.s.playing || !d) return finish();
    this.setDeckGain(d, 0, seconds);
    this.stopping = window.setTimeout(finish, seconds * 1000 + 50);
  }

  private startTicking() {
    if (this.timer !== null) return;
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  /** Where Play starts the current item when its deck is empty (after `restore`), else its Cue In. */
  private resumeAt: number | null = null;

  /**
   * Carry on a queue from elsewhere (another window taking over): the same items,
   * the same one current, from `position` -- playing, faded in over `fadeIn`
   * seconds, or ready there for Play.
   */
  async restore(snap: { queue: QueueItem[]; index: number; position: number; playing: boolean }, fadeIn = 0.6) {
    this.stopAll();
    this.history = [];
    const index = snap.index >= 0 && snap.index < snap.queue.length ? snap.index : -1;
    for (const d of this.decks) {
      if (!d) continue;
      d.el.removeAttribute("src");
      d.el.load();
      d.key = null;
    }
    const item = index >= 0 ? snap.queue[index] : null;
    this.set({ queue: snap.queue, index, playing: false, loading: false, error: null, position: snap.position, duration: item?.track.duration ?? 0 });
    if (!item) return;
    if (snap.playing) await this.jump(index, { fade: fadeIn > 0, seconds: fadeIn, at: snap.position });
    else this.resumeAt = snap.position;
  }

  private stopAll() {
    for (const d of this.decks) if (d) d.el.pause();
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }

  /** Let go of played items beyond `keepPlayed` (see PlayerOptions), in batches. */
  private trimPlayed() {
    const keep = this.o.keepPlayed;
    if (keep === undefined || this.jumping > 0 || this.fading || this.s.index <= keep + TRIM_BATCH) return;
    const drop = this.s.index - keep;
    const gone = this.s.queue.slice(0, drop).map((x) => x.key);
    const queue = this.s.queue.slice(drop);
    const alive = new Set(queue.map((x) => x.key));
    this.history = this.history.map((h) => h - drop).filter((h) => h >= 0);
    this.overlaid = new Set([...this.overlaid].filter((k) => alive.has(k.slice(0, k.lastIndexOf(":")))));
    this.set({ queue, index: this.s.index - drop });
    bus.emit(`${this.o.events}.trimmed`, { keys: gone });
  }

  private tick() {
    this.trimPlayed();
    const d = this.decks[this.active];
    const cur = this.s.current;
    if (!d || !cur) return;
    // Cued and waiting (Live Assist, or after Stop): nothing loaded yet to read.
    if (!d.el.getAttribute("src")) return;
    const position = d.el.currentTime;
    const duration = Number.isFinite(d.el.duration) && d.el.duration > 0 ? d.el.duration : cur.track.duration;
    if (Math.abs(position - this.s.position) > 0.1 || duration !== this.s.duration) this.set({ position, duration });
    if (!this.s.playing || this.fading || this.s.stopAfter) return;
    // Where this item ends: its Early Out (the detected one is its last audible
    // sound), else the end of the file. With a crossfade the next item starts that
    // long before it, so the two overlap and this one has faded out by its end;
    // without one, the next starts there, gapless. (Before 2026-10-28 the next
    // started *at* the end, under a song that had already finished: no overlap.)
    const endAt = cur.track.cueOut !== null && cur.track.cueOut > position ? cur.track.cueOut : duration;
    // With a crossfade, a song's own mix point (the Liquidsoap-style cues) moves the
    // hand-over there; the two then overlap until its end (core/handover.ts).
    const mix = mixPoint({ ...cur.track, cueOut: endAt, duration }, this.s.crossfade);
    const out = mix ?? endAt - this.s.crossfade;
    const overlap = mix !== null ? endAt - mix : this.s.crossfade;
    // A clip due over this song starts at its point.
    cur.overlays?.forEach((o, i) => {
      const key = `${cur.key}:${i}`;
      if (!this.overlaid.has(key) && position >= o.at && position < o.at + 5) {
        this.overlaid.add(key);
        void this.playOver(o);
      }
    });
    // A song shortened to land on time fades out over its last seconds instead of stopping.
    if (cur.fadeOut && this.fadedKey !== cur.key && position >= endAt - cur.fadeOut) {
      this.fadedKey = cur.key;
      this.setDeckGain(d, 0, Math.max(0.1, endAt - position));
    }
    // Live Assist, or the next item marked to wait: this one fades out over the
    // crossfade, ending at its end, rather than stopping dead; then the next is made ready.
    const waits = this.nextWaits();
    if (waits && this.s.crossfade > 0 && this.fadedKey !== cur.key && position >= out
      && position > (cur.track.cueIn ?? 0) + 5) {
      this.fadedKey = cur.key;
      this.setDeckGain(d, 0, Math.max(0.1, endAt - position));
    }
    // An item with a mix out ends there and the next one waits; without one it plays
    // to its end (onEnded).
    if (waits && cur.track.cueOut !== null && position >= cur.track.cueOut && position > (cur.track.cueIn ?? 0) + 5) {
      return this.cueNext();
    }
    const ready = (this.s.crossfade > 0 || cur.track.cueOut !== null) && !waits;
    if (ready && position >= out && out > (cur.track.cueIn ?? 0) + 5) {
      const n = this.pickNext();
      if (n === null || (n === this.s.index && this.s.repeat !== "one")) return;
      this.fading = true;
      void this.jump(n, { fade: this.s.crossfade > 0, seconds: this.s.crossfade > 0 ? overlap : undefined });
    }
  }

  private installMediaKeys() {
    const ms = typeof navigator !== "undefined" ? navigator.mediaSession : undefined;
    if (!ms) return;
    const safe = (action: MediaSessionAction, fn: MediaSessionActionHandler) => {
      try {
        ms.setActionHandler(action, fn);
      } catch {
        // Not supported in this webview.
      }
    };
    safe("play", () => this.play());
    safe("pause", () => this.pause());
    safe("nexttrack", () => this.next());
    safe("previoustrack", () => this.prev());
    safe("seekto", (d) => d.seekTime !== undefined && this.seek(d.seekTime));
  }

  private updateMediaSession() {
    const ms = typeof navigator !== "undefined" ? navigator.mediaSession : undefined;
    const cur = this.s.current;
    if (!ms || typeof MediaMetadata === "undefined") return;
    ms.metadata = cur
      ? new MediaMetadata({
          title: cur.track.title,
          artist: cur.track.artist,
          album: cur.track.album ?? "",
          artwork: [{ src: withToken(`${engineBase()}/api/track/art?id=${encodeURIComponent(cur.track.id)}`) }],
        })
      : null;
  }
}

/** The player the library plays on: Songs, Flow, playlists, Compact, Pro. */
export const player = new Player();

/** A player of a plugin's own (Radio's On air), with its own queue, settings and output. */
export function createPlayer(options: Omit<PlayerOptions, "shared" | "mediaKeys" | "fadeIn"> & Partial<Pick<PlayerOptions, "shared" | "mediaKeys" | "fadeIn">>): Player {
  return new Player({ shared: false, mediaKeys: false, fadeIn: true, ...options });
}

export type { Player };

/** A player's state, kept current: the library's by default, or another (`usePlayer(onAir)`). */
export function usePlayer(of: Player = player): PlayerSnapshot {
  return useSyncExternalStore(of.subscribe, of.get, of.get);
}

/** Two plain values or flat objects with the same fields, compared field by field. */
function sameFields(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/**
 * Part of a player's state, kept current: the screen redraws only when what `select`
 * picks changes (compared field by field), not on every position step. A part that needs
 * the position coarsely picks it rounded (`Math.floor(s.position)`).
 */
export function usePlayerWith<T>(of: { subscribe: (fn: () => void) => () => void; get: () => PlayerSnapshot }, select: (s: PlayerSnapshot) => T): T {
  const cache = useRef<{ value: T } | null>(null);
  const pick = useRef(select);
  pick.current = select;
  const read = () => {
    const next = pick.current(of.get());
    if (cache.current && sameFields(cache.current.value, next)) return cache.current.value;
    cache.current = { value: next };
    return next;
  };
  return useSyncExternalStore(of.subscribe, read, read);
}
